import { forwardRef, ReactNode, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useVariant } from "@~/contexts/VariantContext/VariantContext";
import { isM2MFabricProduct } from "./components/M2MProductSelector/utils";
import { M2MProductSelector } from "./components/M2MProductSelector/M2MProductSelector";
import { Product } from "../../../types/types";
import styles from "./PDPLink.module.scss";
import { getProductUrl } from "@~/utils/product";
import { useProductCardEvent } from "@~/contexts/ProductCardEventContext/ProductCardEventContext.tsx";
import { productCardEvents } from "@~/contexts/ProductCardEventContext/productCardEvents";

type PDPLinkProps = {
  product: Product;
  children: ReactNode;
  className?: string;
  componentName?: string;
  tabIndex?: number;
  testId?: string;
  ariaHidden?: boolean;
  ariaLabel?: string;
  noFollow?: boolean;
};

export const PDPLink = forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  PDPLinkProps
>(
  (
    {
      product,
      children,
      className,
      componentName,
      tabIndex,
      testId,
      ariaHidden,
      ariaLabel,
      noFollow = false,
    },
    ref,
  ) => {
    const [showM2MModal, setShowM2MModal] = useState(false);
    const { variant } = useVariant();
    const isM2MProduct = isM2MFabricProduct(product);
    const portalRef = useRef(null);
    const dispatchEvent = useProductCardEvent();

    const handleM2MClick = () => {
      window.dispatchEvent(new CustomEvent("closeAllM2MModals"));
      setShowM2MModal(true);
    };

    const handleCloseModal = () => {
      setShowM2MModal(false);
    };

    useEffect(function subscribeToM2MModalCloseEvents() {
      const handleM2MModalClose = () => setShowM2MModal(false);
      window.addEventListener("closeAllM2MModals", handleM2MModalClose);

      return function unsubscribeFromM2MModalCloseEvents() {
        window.removeEventListener("closeAllM2MModals", handleM2MModalClose);
      };
    }, []);

    if (isM2MProduct) {
      return (
        <>
          <div ref={portalRef} className="m2mModalPortalReference" />
          <button
            ref={ref as React.Ref<HTMLButtonElement>}
            className={classNames(styles.container, className)}
            onClick={handleM2MClick}
            data-component={componentName ? `pcl-${componentName}` : undefined}
            type="button"
            tabIndex={tabIndex}
            aria-haspopup="dialog"
            {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
          >
            {children}
          </button>
          {showM2MModal && (
            <M2MProductSelector
              isOpen={showM2MModal}
              onClose={handleCloseModal}
              pdpUrl={variant?.pdpURL.url || ""}
            />
          )}
        </>
      );
    }

    return (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={getProductUrl(product, variant)}
        className={classNames(styles.container, className)}
        data-component={componentName ? `pcl-${componentName}` : undefined}
        data-testid={testId ?? undefined}
        tabIndex={tabIndex}
        aria-hidden={ariaHidden}
        aria-label={ariaLabel}
        {...(noFollow && { rel: "nofollow" })}
        onClick={() => dispatchEvent(productCardEvents.cardClick)}
      >
        {children}
      </a>
    );
  },
);

PDPLink.displayName = "PDPLink";
